import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@common-modules/shared/auth/services/auth.guard';
import { LeavePageGuard } from '@common-modules/shared/navigation/leave-page.guard';
import { HistoricalAlarmsGridComponent } from '@common-modules/wlm-grid/historical-alarms/historical-alarms-grid.component';
import { RouteNameHelper } from '@water-loss/features/navigation/route-name-helper';
import { RaiseActivityPageComponent } from './features/alc/raise-activity-page/raise-activity-page.component';
import { SAWizardPageComponent } from './features/configuration/sa-wizard-page/sa-wizard-page.component';
import { WlmNavKeys } from './features/navigation/wlm-nav-keys';

const routes: Routes = [
  { path: '', redirectTo: 'home', pathMatch: 'full', canLoad: [AuthGuard] },
  {
    path: 'alarms/activation/:elementId',
    component: HistoricalAlarmsGridComponent,
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
  },
  {
    path: 'alarms/activation/:elementId/:startDate',
    component: HistoricalAlarmsGridComponent,
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
  },
  {
    path: 'alarms/activation/:elementId/:startDate/:endDate',
    component: HistoricalAlarmsGridComponent,
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
  },
  {
    path: 'alc',
    loadChildren: () =>
      import(/* webpackChunkName: "alc_" */ `./features/alc/alc.module`).then((m) => m.ALCModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.AlcModule,
    },
  },
  {
    path: 'map',
    loadChildren: () =>
      import(/* webpackChunkName: "map_" */ `./features/map/map.module`).then((m) => m.MapModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.DataAccessModule,
    },
  },
  {
    path: 'customer',
    loadChildren: () =>
      import(/* webpackChunkName: "customer_" */ `./features/customer/customer.module`).then(
        (m) => m.CustomerModule,
      ),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.DataAccessModule,
    },
  },
  {
    path: 'home',
    loadChildren: () =>
      import(/* webpackChunkName: "home_" */ `./features/home/home.module`).then(
        (m) => m.HomeModule,
      ),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
  },
  {
    path: 'dn',
    loadChildren: () =>
      import(
        /* webpackChunkName: "distribution-network_" */ `./features/dn/distribution-network.module`
      ).then((m) => m.DistributionNetworkModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.LeakageCalculationModule,
    },
  },
  {
    path: 'tn',
    loadChildren: () =>
      import(
        /* webpackChunkName: "transmission-network_" */ `./features/tn/transmission-network.module`
      ).then((m) => m.TransmissionNetworkModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.LeakageCalculationModule,
    },
  },
  {
    path: 'pressure-management',
    loadChildren: () =>
      import(
        /* webpackChunkName: "pressure-management_" */ `./features/pressure-management/pressure-management.module`
      ).then((m) => m.PressureManagementModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.PressureManagementModule,
    },
  },
  {
    path: 'roles',
    loadChildren: () =>
      import(
        /* webpackChunkName: "wlm-security_" */ `@common-modules/wlm-security/wlm-security.module`
      ).then((m) => m.WlmSecurityModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.AdministrationModule,
    },
  },
  {
    path: 'water-balance',
    loadChildren: () =>
      import(
        /* webpackChunkName: "water-balance_" */ `./features/water-balance/water-balance.module`
      ).then((m) => m.WaterBalanceModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.WaterBalanceModule,
    },
  },
  {
    path: 'alc/activity',
    component: RaiseActivityPageComponent,
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
  },
  {
    path: `${RouteNameHelper.configurationModule}/hierarchy`,
    loadChildren: () =>
      import(/* webpackChunkName: "hierarchy_" */ `./features/hierarchy/hierarchy.module`).then(
        (m) => m.HierarchyModule,
      ),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.ConfigurationModule,
    },
  },
  {
    path: `${RouteNameHelper.configurationModule}/${RouteNameHelper.mergedZones}`,
    loadChildren: () =>
      import(
        /* webpackChunkName: "merged-zones_" */ `./features/merged-zones/merged-zones.module`
      ).then((m) => m.MergedZonesModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.ConfigurationModule,
    },
  },
  {
    path: RouteNameHelper.configurationModule,
    loadChildren: () =>
      import(
        /* webpackChunkName: "configuration_" */ `./features/configuration/configuration.module`
      ).then((m) => m.ConfigurationModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.ConfigurationModule,
    },
  },
  {
    path: 'dv/leakage-reporting',
    loadChildren: () =>
      import(
        /* webpackChunkName: "leakage-reporting_" */ `./features/leakage-reporting/leakage-reporting.module`
      ).then((m) => m.LeakageReportingModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.DataAccessModule,
    },
  },
  {
    path: RouteNameHelper.dataValidation,
    loadChildren: () =>
      import(
        /* webpackChunkName: "data-validation_" */ `./features/data-validation/data-validation.module`
      ).then((m) => m.DataValidationModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.DataAccessModule,
    },
  },
  {
    path: 'dv',
    loadChildren: () =>
      import(
        /* webpackChunkName: "data-visualization_" */ `./features/data-visualization/data-visualization.module`
      ).then((m) => m.DataVisualizationModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.DataAccessModule,
    },
  },
  {
    path: 'alarm-creation',
    component: SAWizardPageComponent,
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.AlarmsCreation,
    },
  },
  {
    path: RouteNameHelper.notifications,
    loadChildren: () =>
      import(/* webpackChunkName: "monitoring_" */ `./features/monitoring/monitoring.module`).then(
        (m) => m.MonitoringModule,
      ),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.MonitoringModule,
    },
  },
  {
    path: RouteNameHelper.bi,
    loadChildren: () =>
      import(/* webpackChunkName: "bi_" */ `./features/bi/bi.module`).then((m) => m.BiModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.BiModule,
    },
  },
  {
    path: 'schematics',
    loadChildren: () =>
      import(/* webpackChunkName: "schematics_" */ `./features/schematics/schematics.module`).then(
        (m) => m.SchematicsModule,
      ),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      // navKey: WlmNavKeys.Schematics,
    },
  },
  {
    path: 'logs',
    loadChildren: () =>
      import(/* webpackChunkName: "logs_" */ `./features/logs/logs.module`).then(
        (m) => m.LogsModule,
      ),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.LogsModule,
    },
  },
  {
    path: 'theme-test',
    loadChildren: () =>
      import(`./features/theme-test/theme-test.module`).then((m) => m.ThemeTestModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {},
  },
  {
    path: 'integration',
    loadChildren: () =>
      import(
        /* webpackChunkName: "integration_" */ `./features/integration/integration.module`
      ).then((m) => m.IntegrationModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      superUser: true,
    },
  },

  {
    path: 'validation',
    loadChildren: () =>
      import(/* webpackChunkName: "validation_" */ `./features/validation/validation.module`).then(
        (m) => m.ValidationModule,
      ),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    data: {
      navKey: WlmNavKeys.ValidationModule,
    },
  },

  { path: '**', redirectTo: '' },
];

@NgModule({
  declarations: [],
  imports: [CommonModule, RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
  providers: [LeavePageGuard],
})
export class WaterLossRoutingModule {}
